// Copyright 2019 Sorama B.V.

import { PLATFORM } from "aurelia-pal";
import { NavigationInstruction, RouteConfig, activationStrategy } from "aurelia-router";
import { IWorkflowSettings } from "libraries/WorkflowFramework/Interfaces/IWorkflowSettings";
import { AnalysisWorkflowModuleCatalog } from "./WorkflowModuleCatalog/AnalysisWorkflowModuleCatalog";
import { ClientWorkflowModuleCatalog } from "./WorkflowModuleCatalog/ClientWorkflowModuleCatalog";
import { IndustrialReportWorkflowModuleCatalog } from "./WorkflowModuleCatalog/IndustrialReportWorkflowModuleCatalog";
import { MeasurementWorkflowModuleCatalog } from "./WorkflowModuleCatalog/MeasurementModuleCatalog";
import { MeasurementSetupWorkflowModuleCatalog } from "./WorkflowModuleCatalog/MeasurementSetupWorkflowModuleCatalog";
import { PresentationWorkflowModuleCatalog } from "./WorkflowModuleCatalog/PresentationWorkflowModuleCatalog";
import { ProjectModuleCatalog } from "./WorkflowModuleCatalog/ProjectModuleCatalog";
import { StreamerWorkflowModuleCatalog } from "./WorkflowModuleCatalog/StreamerWorkflowModuleCatalog";
import { UserWorkflowModuleCatalog } from "./WorkflowModuleCatalog/UserWorkflowModuleCatalog";

export enum RouteAuthorizationMode {
    /* Specifies that this workflow may be accessed without authorization */
    Unauthorized = 0,

    /* Specifies that this workflow may should be accessed with authorization if online */
    OnlineAuthorized = 1,

    /* Specifies that this workflow must always be accessed with authorization */
    Authorized = 2,
}

export class RouteConfigCatalog {
    private static workflowShell = "modules/shell/WorkflowView/WorkflowViewShell";
    private static lastInstruction: NavigationInstruction;

    static navigationExternalLink(instruction: NavigationInstruction) {
        window.open(instruction.config.link, "_blank").focus();
        instruction.config.activationStrategy = activationStrategy.noChange;
        this.lastInstruction = instruction;
    }

    static navigationWorkflow(instruction: NavigationInstruction) {
        instruction.config.moduleId = PLATFORM.moduleName("libraries/WorkflowFramework/WorkflowRouter/WorkflowRouter");
        instruction.config.activationStrategy = activationStrategy.replace;

        // This piece of code was a Workaround for Aurelia bug: https://github.com/aurelia/router/issues/639 to parse a query string when login in. However, this has some side effects
        // with other flows (esp. timeline entries restoring). For now, I propose to keep this commented code.
        // if(instruction.parentInstruction == null && this.lastInstruction != undefined && this.lastInstruction.fragment == instruction.fragment
        //         && Object.keys(instruction.queryParams).length == 0 && Object.keys(this.lastInstruction.queryParams).length > 0)
        // {
        //     instruction.router.navigate(instruction.fragment + "?" + this.lastInstruction.queryString, { trigger: true, replace: true });
        //     return;
        // }

        this.lastInstruction = instruction;
    }

    static get catchAllRoute(): RouteConfig {
        return {
            // Invalid Route
            route: "invalidroute",
            moduleId: "modules/static/InvalidRoute/InvalidRouteView",
            nav: false,
            title: "Invalid Route",
            settings: {
                auth: RouteAuthorizationMode.Unauthorized,
                licenseCheck: false,
                clientAccess: false,
            },
        };
    }

    static get routes(): RouteConfig[] {
        const catalog: RouteConfig[] = [
            //#region Static Workflows
            // Home
            {
                route: ["", "home"],
                name: "home",
                activationStrategy: "replace",
                moduleId: "modules/static/Home/HomeView",
                nav: false,
                title: "Home",
                settings: {
                    auth: RouteAuthorizationMode.Unauthorized,
                    licenseCheck: false,
                    clientAccess: false,
                },
            },

            // Route that receives unauthorized navigation
            {
                route: ["unauthorized"],
                name: "unauthorized",
                moduleId: "modules/static/Unauthorized/UnauthorizedRedirect",
                nav: false,
                settings: {
                    needsWorkflowMode: false,
                    auth: RouteAuthorizationMode.Unauthorized,
                    licenseCheck: false,
                    clientAccess: false,
                },
            },

            {
                // Invalid Membership
                route: "invalidmembership",
                moduleId: "modules/static/InvalidMembership/InvalidMembership",
                nav: false,
                title: "Invalid Membership",
                settings: {
                    needsWorkflowMode: false,
                    auth: RouteAuthorizationMode.Unauthorized,
                    licenseCheck: false,
                    clientAccess: false,
                },
            },
            // Workflow Mode Selection Mage
            {
                title: "Workflow Mode Selection",
                route: "WorkflowModeSelection",
                name: "workflow_mode_selection",
                activationStrategy: "replace",
                moduleId: PLATFORM.moduleName("modules/static/WorkflowModeMage/WorkflowModeMage"),
                nav: false,
                settings: {
                    needsWorkflowMode: false,
                    auth: RouteAuthorizationMode.Unauthorized,
                    licenseCheck: false,
                    clientAccess: false,
                },
            },
            {
                route: "GuidanceEditor",
                moduleId: PLATFORM.moduleName("modules/static/GuidanceEditor/GuidanceEditor"),
                activationStrategy: "replace",
                nav: false,
                title: "Guidance Editor",
                name: "guidance_editor",
                settings: {
                    icon: "fas fa-edit",
                    auth: RouteAuthorizationMode.Unauthorized,
                    licenseCheck: false,
                    clientAccess: false,
                },
            },
            // Route that receives unauthorized navigation
            {
                route: ["webshop"],
                name: "webshop",
                moduleId: "modules/static/Webshop/Webshop",
                nav: false,
                settings: {
                    needsWorkflowMode: false,
                    auth: RouteAuthorizationMode.Authorized,
                    licenseCheck: false,
                    clientAccess: false,
                    disableNavBar: true,
                    disableProgressBar: true,
                    disableHardwareSelection: true,
                    disableServicesStatus: true,
                    disableCredits: true,
                    disableWorkflowNavigation: true,
                },
            },
            //#endregion

            //#region Sidebar Workflows
            // Notification Workflow
            {
                route: "Notification",
                nav: false,
                navigationStrategy: RouteConfigCatalog.navigationWorkflow,
                title: "Notifications",
                settings: {
                    auth: RouteAuthorizationMode.Unauthorized,
                    licenseCheck: false,
                    clientAccess: false,
                    workflowSettings: <IWorkflowSettings>{
                        workflowName: "Notification",
                        defaultStartModules: UserWorkflowModuleCatalog.NotificationModule.name,
                    },
                },
            },

            // User Management Workflow
            {
                route: "UserManagement",
                navigationStrategy: RouteConfigCatalog.navigationWorkflow,
                nav: false,
                title: "User",
                settings: {
                    auth: RouteAuthorizationMode.Unauthorized,
                    licenseCheck: false,
                    clientAccess: false,
                    workflowSettings: <IWorkflowSettings>{
                        workflowName: "UserManagement",
                        defaultStartModules: UserWorkflowModuleCatalog.UserInformationModule.name,
                    },
                },
            },

            // Server Workflow
            {
                route: "Server",
                navigationStrategy: RouteConfigCatalog.navigationWorkflow,
                nav: false,
                title: "Server",
                settings: {
                    auth: RouteAuthorizationMode.Unauthorized,
                    licenseCheck: false,
                    clientAccess: false,
                    workflowSettings: <IWorkflowSettings>{
                        workflowName: "Server",
                    },
                },
            },

            // Client Workflow
            {
                route: "Client",
                navigationStrategy: RouteConfigCatalog.navigationWorkflow,
                nav: false,
                title: "Client",
                settings: {
                    auth: RouteAuthorizationMode.Unauthorized,
                    licenseCheck: false,
                    clientAccess: false,
                    workflowSettings: <IWorkflowSettings>{
                        workflowName: "Client",
                    },
                },
            },
            //#endregion

            //#region Regular Workflows
            // Streamer Workflow
            {
                route: "Streamer",
                name: "streamer",
                navigationStrategy: RouteConfigCatalog.navigationWorkflow,
                nav: 100,
                title: "Stream",
                settings: {
                    icon: "fas fa-video",
                    name: "Stream live sound images",
                    auth: RouteAuthorizationMode.OnlineAuthorized,
                    licenseCheck: false,
                    clientAccess: true,
                    description:
                        "Stream live sound images from your Sorama CAM to your browser. Also applicable to Far Field and Near Field measurement setups.",
                    workflowSettings: <IWorkflowSettings>{
                        workflowName: "Streamer",
                        defaultStartModules: StreamerWorkflowModuleCatalog.StreamerModule.name,
                    },
                },
            },

            // Measurement Workflows
            {
                route: "Recordings",
                name: "Recordings",
                navigationStrategy: RouteConfigCatalog.navigationWorkflow,
                nav: 90,
                title: "Manage",
                settings: {
                    icon: "fas fa-list",
                    name: "Manage my measurements",
                    auth: RouteAuthorizationMode.OnlineAuthorized,
                    licenseCheck: false,
                    clientAccess: true,
                    description: "Manage the measurements on your hard disk or upload measurements to the Cloud.",
                    workflowSettings: <IWorkflowSettings>{
                        workflowName: "Recordings",
                        defaultStartModules: [
                            MeasurementWorkflowModuleCatalog.ProjectManagementModule.name,
                            MeasurementWorkflowModuleCatalog.MeasurementsListModule.name,
                            MeasurementWorkflowModuleCatalog.ManageCamIV64FileModule.name,
                        ],
                    },
                },
            },
            {
                route: "Measurement",
                name: "measurement",
                navigationStrategy: RouteConfigCatalog.navigationWorkflow,
                nav: 110,
                title: "Measure",
                settings: {
                    icon: "fas fa-dot-circle",
                    name: "Do a sound measurement",
                    auth: RouteAuthorizationMode.OnlineAuthorized,
                    licenseCheck: false,
                    clientAccess: true,
                    description: "Perform a sound measurement. The resulting measurement can be analyzed.",
                    workflowSettings: <IWorkflowSettings>{
                        workflowName: "Measurement",
                        defaultStartModules: MeasurementWorkflowModuleCatalog.MeasurementPreparationModule.name,
                    },
                },
            },

            {
                route: "Analysis",
                navigationStrategy: RouteConfigCatalog.navigationWorkflow,
                name: "analysis",
                nav: 120,
                title: "Analyze",
                settings: {
                    icon: "fas fa-flask",
                    name: "Analyze existing measurements",
                    auth: RouteAuthorizationMode.Authorized,
                    licenseCheck: true,
                    clientAccess: true,
                    description:
                        "Analyze Cloud measurements. Do Time & Spectral Analysis, Far Field Analysis and Near Field Analysis.",
                    workflowSettings: <IWorkflowSettings>{
                        workflowName: "Analysis",
                        defaultStartModules: AnalysisWorkflowModuleCatalog.AnalysisOverviewModule.name,
                    },
                },
            },
            {
                route: "Reporting",
                navigationStrategy: RouteConfigCatalog.navigationWorkflow,
                name: "Reporting",
                nav: 131,
                title: "Reporting",
                settings: {
                    icon: "fas fa-file-chart-line",
                    name: "Export reports of your measurements",
                    auth: RouteAuthorizationMode.Authorized,
                    licenseCheck: true,
                    clientAccess: true,
                    description: "Industrial reporting.",
                    workflowSettings: <IWorkflowSettings>{
                        workflowName: "Reporting",
                        defaultStartModules: [IndustrialReportWorkflowModuleCatalog.ReportSelectionModule.name],
                    },
                },
            },
            {
                route: "Presentations",
                name: "Presentations",
                navigationStrategy: RouteConfigCatalog.navigationWorkflow,
                nav: 150,
                title: "Present",
                settings: {
                    icon: "fas fa-presentation",
                    name: "Create presentations",
                    auth: RouteAuthorizationMode.Unauthorized,
                    licenseCheck: true,
                    clientAccess: true,
                    description: "Create and manage presentations showing important analysis results.",
                    workflowSettings: <IWorkflowSettings>{
                        workflowName: "Presentations",
                        defaultStartModules: [
                            PresentationWorkflowModuleCatalog.CanvasAddModule.name,
                            PresentationWorkflowModuleCatalog.presentationPageModule.name,
                        ],
                    },
                },
            },
            {
                route: "Journals",
                name: "Journals",
                navigationStrategy: RouteConfigCatalog.navigationWorkflow,
                nav: false,
                title: "Journal",
                settings: {
                    icon: "fas fa-book",
                    name: "Create journals",
                    auth: RouteAuthorizationMode.Unauthorized,
                    licenseCheck: true,
                    clientAccess: true,
                    description: "Create and manage journals showing important analysis results.",
                    workflowSettings: <IWorkflowSettings>{
                        workflowName: "Journals",
                        defaultStartModules: [
                            PresentationWorkflowModuleCatalog.CanvasAddModule.name,
                            ProjectModuleCatalog.journalModule.name,
                        ],
                    },
                },
            },

            //#endregion

            //#region Other Workflows

            // Measurement Setup Management
            {
                route: "MeasurementSetups",
                navigationStrategy: RouteConfigCatalog.navigationWorkflow,
                nav: false,
                title: "Measurement Setups",
                settings: {
                    auth: RouteAuthorizationMode.Unauthorized,
                    licenseCheck: false,
                    clientAccess: true,
                    workflowSettings: <IWorkflowSettings>{
                        workflowName: "MeasurementSetups",
                        defaultStartModules: [
                            MeasurementSetupWorkflowModuleCatalog.MeasurementSetupsModule.name,
                            MeasurementSetupWorkflowModuleCatalog.MeasurementSetupModule.name,
                        ],
                    },
                },
            },
            {
                route: "MotionDevice",
                navigationStrategy: RouteConfigCatalog.navigationWorkflow,
                nav: false,
                title: "Motion Device",
                settings: {
                    auth: RouteAuthorizationMode.Unauthorized,
                    licenseCheck: false,
                    clientAccess: true,
                    workflowSettings: <IWorkflowSettings>{
                        workflowName: "MotionDevice",
                        defaultStartModules: ClientWorkflowModuleCatalog.MotionRestApiModule.name,
                    },
                },
            },

            {
                route: "DeviceManagement",
                navigationStrategy: RouteConfigCatalog.navigationWorkflow,
                nav: false,
                title: "Legacy Device Management",
                settings: {
                    auth: RouteAuthorizationMode.Unauthorized,
                    licenseCheck: false,
                    clientAccess: true,
                    workflowSettings: <IWorkflowSettings>{
                        workflowName: "DeviceManagement",
                        defaultStartModules: ClientWorkflowModuleCatalog.DeviceManagementModule.name,
                    },
                },
            },

            //#endregion
        ];
        return catalog;
    }
}
